<!-->╔══════════════════════════════════════════════════════════════════════════════════════╗<- ->
<- ->║                             Frequent Flyer Dashboard                                 ║<- ->
<- ->╚══════════════════════════════════════════════════════════════════════════════════════╝<--->

<template>
    <div class="rewards-page grid" v-if="loaded" v-show="$route.path != '/'">

        <div v-if="$screen.isMobile" class="nav-menu-toggle">
            <span :class="{'nav-menu-open':1, active:mobileNavShowing}" @click="showMobileNav()">
                <span>
                    <span class="welcome-message">{{ greetingMessage }}</span>
                    <strong v-if="member">{{ member.firstName }} {{ member.lastName }}</strong>
                </span>
            </span>
        </div>

        <!-- PAGE CONTENT -->
        <loading :is-loading="loading" @try-again="routeTo($route.path, true)">
            <div class="section-rewards-content">
                <router-view :class="{ submitting }"
                             :member-details.sync="memberDetails" 
                             :portal-info="portalInfo"
                             @loading="loading = true" @loaded="loading = false"
                             @submitting="submitting = true" @submitted="submitting = false"
                             @close="onViewClose">
                </router-view>
            </div>
        </loading>

        <!-- SIDE MENU -->
        <div :class="{'sticky-side-nav':1, active:mobileNavShowing}">
            <span v-if="$screen.isMobile" class="nav-menu-close" @click="hideMobileNav()"><strong>Close</strong></span>

            <ul class="side-nav">
                <template v-if="showGreeting && member">
                    <li class="menu-greeting" v-if="true">
                        <ul>
                            <li class="welcome-message">
                                <span>{{ greetingMessage }}</span>
                                <strong>{{ member.firstName }} {{ member.lastName }}</strong>
                            </li>
                        </ul>
                    </li>
                </template>

                <li v-for="tab in activeTabs" :class="tab.className">
                    <router-link :to="tab.path" :class="{ button: !$screen.isMobile }" :key="tab.path">{{ tab.title }}</router-link>
                </li>

                <li v-if="member && account" class="menu-signout">       
                    <a @click="doLogout(true)" :class="{ 'button': !$screen.isMobile }">Logout</a>
                </li>
            </ul>
        </div>

        <!-- SESSION TIMEOUT WARNING -->
        <login-watcher v-show="$route.path != '/login'"
                       @log-in="doLogout(true)"
                       @session-started="routeTo('/account')"
                       @session-ended="doLogout(true)"
                       @session-continue="refreshSession">
        </login-watcher>

    </div>
</template>

<style>
    .submitting:before {
        display: block;
        background: rgba(0,0,0,0.1);
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        content: '';
        animation: progress 5s ease infinite;
    }

    .submitting {
        opacity: 0.5;
        position: relative;
    }

    @keyframes progress {
        0% {
            background: transparent;
        }

        50% {
            background: rgba(0,0,0,0.1);
        }

        100% {
            background: transparent;
        }
    }
</style>

<script>
    import { importDefaults } from "@/helpers/imports"
    import { fetchJSON } from "@/helpers/fetch"
    import { isoDate, toDate } from "@/helpers/dates"

    import LoginWatcher from "@/custom/login_watcher"

    import Vue from 'vue'
    import LabeledTextInput from '@/common/labeled_text_input'
    import TransitionExpand from '@/common/transition_expand'
    import DateValue from '@/common/date_value'
    import TimeValue from '@/common/time_value'
    import Loading from '@/common/loading'

    Vue.component("LabeledTextInput", LabeledTextInput)
    Vue.component("TransitionExpand", TransitionExpand)
    Vue.component("DateValue", DateValue)
    Vue.component("TimeValue", TimeValue)
    Vue.component("Loading", Loading)

    //  Use the same route map logic that's defined in /src, but apply our own custom components to it instead:
    import routes from '@/pages/ff_dashboard.routes.js'
    const components = importDefaults(require.context('./ff_dashboard', false))

    export default {
        name: "FrequentFlyerDashboard",
        router: routes(components),
        components: { LoginWatcher },

        emits: ['loading', 'loaded'],

        data() {
            return {
                portalInfo: null,

                mobileNavShowing: false,
                memberDetails: null,
                walletCards: null,

                submitting: false,

                loading: false,
                loaded: false,
            }
        },

        mounted() {
            this.loadAll(true)
        },

        watch: {
            '$route': {
                immediate: true,
                handler(to, from) {
                    this.hideMobileNav()

                    //  Handle invalid tab paths
                    to = to || {}
                    if (to.path != '/' && !(to.matched || []).length)
                        this.routeTo(this.memberDetails ? '/account' : '/login')
                }
            },

            'portalInfo'(newVal) {
                if (newVal) {
                    let gtag = newVal && newVal.googleTag
                    if (gtag) {
                        let headScript = document.createElement('script')
                        let bodyScript = document.createElement('noscript')
                        let bodyIFrame = document.createElement('iframe')

                        headScript.innerText = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtag}');`

                        bodyIFrame.src = "https://www.googletagmanager.com/ns.html?id=" + gtag
                        bodyIFrame.height = "0"
                        bodyIFrame.width = "0"
                        bodyIFrame.style = "display:none;visibility:hidden"
                        bodyScript.appendChild(bodyIFrame)

                        document.head.appendChild(headScript)
                        document.body.appendChild(bodyScript)
                    }
                }
            }
        },

        methods: {
            routeTo(path, force) {
                this.submitting = false
                this.hideMobileNav()

                if (this.$route.path != path)
                    this.$router.push({ path })
                else if (force) {
                    this.$router.push({ path: '/' })
                    setTimeout(() => this.$router.push({ path }))
                }

                //  Going to or from root? Reload everything
                if ([this.$route.path, path].includes('/')) {
                    this.loading = false
                    this.loadAll(true)
                }
            },

            loadAll(redirect) {
                this.$emit('loading')

                return Promise.all([
                    this.getPortalInfo(),
                    this.getMemberDetails(),
                    this.getWallet()
                ])
                    .catch(() => {
                        this.doLogout(false)
                    })
                    .finally(() => {
                        if (redirect && this.$route.path == '/')
                            this.routeTo(this.memberDetails ? '/account' : '/login')

                        this.loaded = true
                        this.$emit('loaded')
                    })
            },

            showMobileNav() {
                this.mobileNavShowing = true;
                document.body.classList.add('dialog-open');
            },
            hideMobileNav() {
                this.mobileNavShowing = false;
                document.body.classList.remove('dialog-open')
            },

            onViewClose() {
                this.routeTo('/')
            },

            doLogout(redirect) {
                this.memberDetails = null
                this.walletCards = null

                if (!redirect)
                    fetchJSON('/api/members/DoLogout')
                else {
                    this.routeTo('/login')
                    this.loading = true

                    fetchJSON('/api/members/DoLogout')
                        .finally(() => {
                            this.loading = false
                        })
                }
            },

            refreshSession() {
                fetchJSON('/api/members/RefreshSession')
            },

            getPortalInfo() {
                return fetchJSON(`/api/members/GetPortalInfo`)
                    .then(data => {
                        this.portalInfo = data
                    })
            },

            getMemberDetails() {
                this.memberDetails = null

                return fetchJSON(`/api/members/GetMemberDetails?verbose=true`)
                    .then(data => {
                        this.memberDetails = data
                    })
            },

            getWallet() {
                this.walletCards = null

                return fetchJSON(`/api/members/GetWallet`)
                    .then(data => {
                        this.walletCards = data.cards;
                    })
            }
        },

        computed: {
            REWARDS() { return (this.CONFIG || {}).RewardsProgram || {} },

            account() { return this.memberDetails && this.memberDetails.account; },
            member() { return this.memberDetails && this.memberDetails.member; },

            greetingMessage() {
                var today = new Date();
                var userHour = today.getHours();
                let message = "Welcome";

                if (userHour < 12) message = "Good morning"
                else if (userHour < 18) message = "Good afternoon"
                else message = "Good evening"

                return message;
            },

            showGreeting() {
                return this.member && this.account && !['/login', '/recover', '/enroll'].includes(this.$route.path)
            },

            currentTierCode() {
                let currentTier = ({ ...this.account }).cardStatus
                if (currentTier) return currentTier

                let tierInfo = ({ ...this.member }).tierInfo
                if (tierInfo && tierInfo[0]) return tierInfo[0].tierName
            },

            walletCardsCount() {
                return (this.walletCards || []).length
            },

            activeTabs() {
                let allRoutes = Object.fromEntries(this.$router.options.routes
                    .filter(route => route.path != '/')
                    .map(route => ({ ...route, key: route.path.split('/')[1] }))
                    .map(route => [
                        route.key,
                        {
                            className: `menu-${route.key}`,
                            path: `/${route.key}`,
                            title: route.meta.title || route.meta.subtitle
                        }
                    ])
                )

                let activeTab = this.$route.path.split('/')[1]
                let showKeys = []

                if (this.showGreeting) {
                    showKeys = "account|history|contact|reset|wallet|flight|redeem|logout".split('|')

                    if (!this.walletCardsCount && activeTab != 'wallet')
                        showKeys = showKeys.filter(key => key != 'wallet')
                }
                else {
                    showKeys = "login|recover|enroll|about|terms".split('|')

                    if (this.member && this.account) {
                        showKeys.splice(showKeys.indexOf('login') + 1, 0, 'account')
                        if (activeTab != 'login')
                            showKeys = showKeys.filter(key => key != 'login')
                    }
                }

                return showKeys.map(key => allRoutes[key]).filter(tab => tab)
            }
        }
    }
</script>